/* eslint-disable camelcase */
import {Mapper} from '@s-ui/domain'

/**
 * @implements Mapper
 */
export class FromEventTrackingDataToEventTrackingDataValueObjectMapper extends Mapper {
  constructor({
    config,
    valueObject,
    fromPageNameToConstantNamingPageName,
    fromPageTrackingJsonToAdvertisementsValueObjectMapper,
    trackingEventAdComplaintMapper,
    trackingEventUserProfileEditMapper,
    trackingEventAdSubscriptionPriceOptionClickedMapper,
    trackingEventFavoriteAdShareMapper,
    trackingEventsInstantOfferMapper,
    trackingEventLandingInstantOfferMapper,
    trackingEventsVisibilityProductsMapper,
    fromCamelToSnakeCase,
    toQueryString,
    pipe
  }) {
    super()
    this._valueObject = valueObject
    this._config = config
    this._culture = this._config.get('DEFAULT_CULTURE')
    const {
      ADS_DETAIL_PAGE_TYPE,
      ADS_LIST_PAGE_TYPE,
      HIT_INFORMATIONS,
      HIT_ORIGINS,
      MESSAGING_EVENT_NAMES,
      RECOMMENDED_TRACKING_EVENT_CONTEXTS,
      REQUEST_STATUS_AI_SUCCESS,
      SEARCH_AI_USED_HIT_ORIGINS_BY_PAGE_NAME,
      TRACKING_DATA_BY_PAGE,
      TRACKING_EVENT_AI_MAPPING,
      TRACKING_EVENT_CONTEXTS,
      TRACKING_EVENT_DATA_DEFAULT_PRODUCT,
      TRACKING_EVENT_NAMES,
      TRACKING_TRANSLATIONS_BY_CULTURE
    } = this._config.get('tracking')

    const {ADS_LIST_PAGE_NAME, ADS_DETAIL_PAGE_NAME, COMPARADOR_PAGE_NAME, NET_ASSISTANT_PAGE_NAME} =
      TRACKING_TRANSLATIONS_BY_CULTURE[this._culture]
    const {COMPARADOR, NET_ASSISTANT, USER_ALERTS_MATCHES} = TRACKING_DATA_BY_PAGE

    this._hitInformations = HIT_INFORMATIONS
    this._hitOrigins = HIT_ORIGINS
    this._searchAiUsedHitOriginsByPageName = SEARCH_AI_USED_HIT_ORIGINS_BY_PAGE_NAME
    this._trackingEventContexts = TRACKING_EVENT_CONTEXTS
    this._recommendedTrackingEventContexts = RECOMMENDED_TRACKING_EVENT_CONTEXTS
    this._trackingEventDataDefaultProduct = TRACKING_EVENT_DATA_DEFAULT_PRODUCT
    this._trackingEventNames = TRACKING_EVENT_NAMES
    this._trackingTranslationsByCulture = TRACKING_TRANSLATIONS_BY_CULTURE
    this._trackingEventAIMapping = TRACKING_EVENT_AI_MAPPING
    this._requestStatusAISuccess = REQUEST_STATUS_AI_SUCCESS
    this._messagingEventNames = MESSAGING_EVENT_NAMES
    this._trackingAdsListPageName = ADS_LIST_PAGE_NAME
    this._trackingAdsDetailPageName = ADS_DETAIL_PAGE_NAME
    this._fromPageTrackingJsonToAdvertisementsValueObjectMapper = fromPageTrackingJsonToAdvertisementsValueObjectMapper
    this._trackingEventAdComplaintMapper = trackingEventAdComplaintMapper
    this._trackingEventUserProfileEditMapper = trackingEventUserProfileEditMapper
    this._trackingEventFavoriteAdShareMapper = trackingEventFavoriteAdShareMapper
    this._trackingEventsInstantOfferMapper = trackingEventsInstantOfferMapper
    this._trackingEventLandingInstantOfferMapper = trackingEventLandingInstantOfferMapper
    this._trackingEventAdSubscriptionPriceOptionClickedMapper = trackingEventAdSubscriptionPriceOptionClickedMapper
    this._trackingEventsVisibilityProductsMapper = trackingEventsVisibilityProductsMapper
    this._fromPageNameToConstantNamingPageName = fromPageNameToConstantNamingPageName
    this._fromCamelToSnakeCase = fromCamelToSnakeCase
    this._toQueryString = toQueryString
    this._pipe = pipe

    this._adsListPageType = ADS_LIST_PAGE_TYPE

    const detailPage = {pageName: ADS_DETAIL_PAGE_NAME, pageType: ADS_DETAIL_PAGE_TYPE}
    const listPage = {pageName: ADS_LIST_PAGE_NAME, pageType: ADS_LIST_PAGE_TYPE}
    const alertsMatchesPage = {pageName: USER_ALERTS_MATCHES.pageName, pageType: USER_ALERTS_MATCHES.pageType}
    const comparatorPage = {pageName: COMPARADOR_PAGE_NAME, pageType: COMPARADOR.page_type}
    const netAssistantPage = {pageName: NET_ASSISTANT_PAGE_NAME, pageType: NET_ASSISTANT.page_type}

    this._defaultEventPage = detailPage
    this._favoritedPagesByOrigin = {
      [HIT_ORIGINS.AD_DETAIL_FAVORITED]: detailPage,
      [HIT_ORIGINS.AD_DETAIL_FAVORITED_PRICE_DROP]: detailPage,
      [HIT_ORIGINS.AD_DETAIL_FAVORITED_TOPBAR]: detailPage,
      [HIT_ORIGINS.AD_LIST_FAVORITED]: listPage,
      [HIT_ORIGINS.AD_LIST_MENU_FAVORITED_PRICEDROP]: listPage,
      [HIT_ORIGINS.ALERT_AD_FAVORITED]: alertsMatchesPage,
      [HIT_ORIGINS.COMPARATOR_AD_FAVORITED]: comparatorPage,
      [HIT_ORIGINS.NET_ASSISTANT_AD_FAVORITED]: netAssistantPage
    }
  }

  /**
   * @method
   * @private
   */
  _mapFilters = filters =>
    Object.keys(filters)
      .sort()
      .reduce(
        (newFilters, filter) => ({
          ...newFilters,
          [this._fromCamelToSnakeCase(filter)]: filters[filter]
        }),
        {}
      )

  /**
   * @method
   * @private
   */
  _parseQueryParams = queryParams =>
    decodeURIComponent(this._toQueryString(queryParams, {arrayFormat: 'repeat', delimiter: ':'})).toLowerCase()

  _mapAdsAndFiltersData({event, ads = [], filters, ...params}) {
    const filtersString = this._pipe(this._mapFilters, this._parseQueryParams)(filters)

    return {
      event,
      data: {
        filters: filtersString,
        ads: ads.map(({id}) => id).join(',')
      }
    }
  }

  _mapComparatorActionClicked({ad, event, origin, listPosition, pageName}) {
    return {
      event,
      data: {
        hit_origin: origin,
        ...(pageName && {page_name: pageName}),
        products: [
          {
            id: ad.id,
            category: 'ads',
            price: 0,
            quantity: 0,
            list_position: listPosition
          }
        ]
      }
    }
  }

  _mapAdDetailButtonClicked({event, hitInformation}) {
    return {
      event,
      data: {
        hit_information: hitInformation
      }
    }
  }

  _mapDetailGalleryClicked({event, origin, hitInformation, hitInformation2}) {
    return {
      event,
      data: {
        hit_origin: origin,
        hit_information: hitInformation,
        hit_information2: hitInformation2
      }
    }
  }

  _mapAdVideoButtonClicked({ad, event, origin}) {
    return {
      event,
      data: {
        hit_origin: origin,
        products: [
          {
            category: 'ads',
            quantity: 0,
            price: 0,
            id: ad.id
          }
        ]
      }
    }
  }

  _mapAdVideoPlayed({ad, event, origin}) {
    if (ad.videos === undefined || ad.videos.length === 0) {
      return {}
    }

    const {position, autoPlay} = ad.videos[0]

    const eventPosition = position === 'FIRST' ? 'primera posicion' : 'ultima posicion'
    return {
      event,
      data: {
        hit_origin: origin,
        hit_information: autoPlay === true ? 'autoplay primera posicion' : eventPosition,
        products: [
          {
            category: 'ads',
            quantity: 0,
            price: 0,
            id: ad.id
          }
        ]
      }
    }
  }

  _mapAdDiscardClick({event, origin}) {
    return {
      event,
      data: {
        hit_origin: origin
      }
    }
  }

  _mapAdDiscard({event, origin}) {
    return {
      event,
      data: {
        hit_origin: origin
      }
    }
  }

  _mapAdToCompareClick({ad, event, origin, listType, extendedTrackingData = {}, context, pageType = 'list'}) {
    const advertisementsValueObject = this._fromPageTrackingJsonToAdvertisementsValueObjectMapper
      .setParams({context, listType})
      .map({ads: [ad], extendedProduct: extendedTrackingData?.product})

    return {
      event,
      data: {
        hit_origin: origin,
        page_type: pageType,
        page_name_origin: listType,
        ad_id: `${ad.id}`,
        filters: extendedTrackingData?.filters || '',
        products: advertisementsValueObject
          .getProducts()
          .map(product => ({...product, list_position: `${product.list_position}`}))
      }
    }
  }

  _mapAdToCompareMenuClick({event, origin, ads = []}) {
    return {
      event,
      data: {
        page_type: 'list',
        hit_origin: origin,
        hit_information: String(ads.length)
      }
    }
  }

  _mapAdToCompareResultsClick({event, origin, listType, ads = []}) {
    return {
      event,
      data: {
        hit_origin: origin,
        hit_information: String(ads.length),
        page_name_origin: listType
      }
    }
  }

  _mapAdUnfavorited({event, origin}) {
    return {
      event,
      data: {
        hit_origin: origin
      }
    }
  }

  _mapDealerVideoPlayed({event, context}) {
    return {
      event,
      data: {
        hit_origin: 'listado de concesionario',
        products: [
          {
            category: 'dealer',
            quantity: 0,
            price: 0,
            id: context
          }
        ]
      }
    }
  }

  _mapDealerStockSharedClicked({event, context}) {
    const {location} = context ?? {}

    return {
      event,
      data: {
        provinces: location?.province,
        provinces_id: location?.provinceId?.toString?.()
      }
    }
  }

  _mapDealerStockShared({event, context}) {
    return {
      event,
      data: {
        products: [
          {
            category: 'dealer',
            id: context?.id
          }
        ]
      }
    }
  }

  _mapAdImpressionsCollectedData({
    ads = [],
    context,
    event,
    filters,
    filtersString = '',
    listOrder,
    listType: listTypeParam,
    vehicles = [],
    ...params
  }) {
    if (context === this._trackingEventContexts.LIST_CAROUSEL_MODEL_DETAIL) {
      const {ADS_PAGE_MODEL_DETAIL_LIST_TYPE} = this._trackingTranslationsByCulture[this._culture]

      const products = vehicles.map(({makeId, modelId}, index) => {
        return {
          ...this._trackingEventDataDefaultProduct,
          id: `${makeId}:${modelId}`,
          list_position: index + 1,
          list_type: ADS_PAGE_MODEL_DETAIL_LIST_TYPE
        }
      })
      return {event, data: {products}}
    }

    if (
      context === this._trackingEventContexts.LIST_CAROUSEL_SUBSCRIPTION_MAKE ||
      context === this._trackingEventContexts.LIST_CAROUSEL_SUBSCRIPTION_RANDOM
    ) {
      const hitInformation =
        context === this._trackingEventContexts.LIST_CAROUSEL_SUBSCRIPTION_MAKE
          ? this._hitInformations.SUBSCRIPTION_CAROUSEL_AD_IMPRESSIONS_COLLECTED_MAKE
          : this._hitInformations.SUBSCRIPTION_CAROUSEL_AD_IMPRESSIONS_COLLECTED_RANDOM

      const {SUBSCRIPTION_CAROUSEL_LIST_TYPE} = this._trackingTranslationsByCulture[this._culture]

      const products = ads.map(({id}, index) => {
        return {
          ...this._trackingEventDataDefaultProduct,
          id,
          list_position: index + 1,
          list_type: SUBSCRIPTION_CAROUSEL_LIST_TYPE
        }
      })

      return {event, data: {hit_information: hitInformation, products}}
    }

    const listType =
      listTypeParam ||
      this._trackingTranslationsByCulture[this._culture][
        `${this._fromPageNameToConstantNamingPageName.map(context)}_TYPE`
      ]

    const {products} = this._fromPageTrackingJsonToAdvertisementsValueObjectMapper
      .setParams({adsTypeName: 'ad_impression', listType})
      .map({ads, filters, ...params})
      .toJSON()

    return {
      event,
      data: {
        filters: filtersString,
        ...(listOrder && {list_order: listOrder}),
        page_name_origin: listType,
        page_type: 'list',
        products
      }
    }
  }

  _mapCarouselClickedEvent({context, event}) {
    if (context === this._trackingEventContexts.LIST_CAROUSEL_CARD_AD) {
      return {
        event,
        data: {
          hit_information: this._hitInformations.SUBSCRIPTION_CAROUSEL_DETAIL_CLICKED,
          hit_origin: this._hitOrigins.SUBSCRIPTION_CAROUSEL_DETAIL_CLICKED
        }
      }
    }
    if (context === this._trackingEventContexts.LIST_CAROUSEL_CARD_NAVIGATION) {
      return {
        event,
        data: {
          hit_information: this._hitInformations.SUBSCRIPTION_CAROUSEL_LISTING_CLICKED,
          hit_origin: this._hitOrigins.SUBSCRIPTION_CAROUSEL_DETAIL_CLICKED
        }
      }
    }
  }

  _mapModelDetailHomeClicked({ad, event}) {
    const {make, makeId, model, modelId} = ad
    return {
      event,
      data: {
        brand: make,
        brand_id: Number(makeId),
        model,
        model_id: Number(modelId)
      }
    }
  }

  _mapRecommendationWidgetClicked({event, recommendation, ads, context}) {
    const {recommendationId, listName, itemId} = recommendation

    const [{ad, position}] = ads

    return {
      event,
      data: {
        recommendationId,
        listName,
        itemId,
        source: this._recommendedTrackingEventContexts[context],
        item: {id: ad.id, rank: position + 1}
      }
    }
  }

  _mapRecommendationWidgetImpression({event, recommendation, ads, context}) {
    const {recommendationId, listName, itemId} = recommendation

    const adsRecommended = ads?.map((ad, index) => {
      return {
        id: ad.id,
        rank: index + 1
      }
    })

    return {
      event,
      data: {
        recommendationId,
        listName,
        itemId,
        source: this._recommendedTrackingEventContexts[context],
        items: adsRecommended
      }
    }
  }

  _mapSignedEventsData({event, origin, context}) {
    const hit_information_origin_mapping = {
      [this._hitOrigins.GOOGLE_ONE_TAP]: () => this._hitInformations.GOOGLE_AUTHENTICATION,
      [this._hitOrigins.DEFAULT_LOGIN]: () => this._hitInformations.STANDARD_LOGIN,
      [this._hitOrigins.SOCIAL_LOGIN]: ({context}) => {
        const context_mapping = {
          [this._trackingEventContexts.GOOGLE_LOGIN]: this._hitInformations.GOOGLE_LOGIN,
          [this._trackingEventContexts.APPLE_LOGIN]: this._hitInformations.SOCIAL_LOGIN_APPLE
        }

        return context_mapping[context] || ''
      }
    }

    return {
      event,
      data: {
        hit_origin: origin,
        hit_information: hit_information_origin_mapping[origin]({context})
      }
    }
  }

  // The page where the event fired, named as its page view names it. The data of the host page wins over the
  // origin because one origin (the list heart) is shared by every list page.
  _getEventPage({extendedTrackingData, origin, pagesByOrigin}) {
    const {page_name: hostPageName, page_type: hostPageType} = extendedTrackingData
    const originPage = pagesByOrigin[origin]

    if (hostPageName) {
      return {pageName: hostPageName, pageType: hostPageType || originPage?.pageType || this._adsListPageType}
    }

    // TODO: data debt. Without page data nor a known origin the page is a guess: detail, the original default.
    return originPage || this._defaultEventPage
  }

  _mapAdFavoritedData({ad, context, event, extendedTrackingData = {}, origin}) {
    const advertisementsValueObject = this._fromPageTrackingJsonToAdvertisementsValueObjectMapper
      .setParams({context, listType: extendedTrackingData.listType})
      .map({ads: [ad], extendedProduct: extendedTrackingData.product})

    const {pageName, pageType} = this._getEventPage({
      extendedTrackingData,
      origin,
      pagesByOrigin: this._favoritedPagesByOrigin
    })

    return {
      event,
      data: {
        ad_id: ad.id,
        brand: ad.make,
        filters: extendedTrackingData.filters || '',
        fuel_type: ad.fuelType,
        hit_origin: origin,
        max_km: ad.km,
        model: ad.model,
        offer_status: ad.offerType?.literal,
        page_name_origin: pageName,
        page_type: pageType,
        has_pricedrop: ad.priceDrop ? 'with_price_drop' : 'without_price_drop',
        has_reservation: Boolean(ad.hasReservation),
        price: ad.price,
        products: advertisementsValueObject.getProducts(),
        province: ad.province || ad.location?.mainProvince,
        ...(extendedTrackingData.hit_information && {hit_information: extendedTrackingData.hit_information})
      }
    }
  }

  _mapAdSharedData({ad, context, event, extendedTrackingData = {}, origin}) {
    const advertisementsValueObject = this._fromPageTrackingJsonToAdvertisementsValueObjectMapper
      .setParams({context, listType: extendedTrackingData.listType})
      .map({ads: [ad], extendedProduct: extendedTrackingData.product})

    // TODO: move from component to domain config
    const LIST_TRACKING_ORIGINS = ['List Ad Shared', 'List 3p Menu Ad Shared']
    const DETAIL_TRACKING_ORIGINS = ['Detail Topbar Ad Shared', 'Detail Ad Shared']

    const getPageTypeByOrigin = ({origin}) => {
      if (DETAIL_TRACKING_ORIGINS.includes(origin)) return 'detail'
      if (LIST_TRACKING_ORIGINS.includes(origin)) return 'list'

      return ''
    }
    const getPageNameOrigin = ({extendedTrackingData, origin}) => {
      if (LIST_TRACKING_ORIGINS.includes(origin)) return this._trackingAdsListPageName
      if (DETAIL_TRACKING_ORIGINS.includes(origin)) return this._trackingAdsDetailPageName

      return extendedTrackingData.product?.list_type
    }

    return {
      event,
      data: {
        ad_id: ad.id,
        brand: ad.make,
        filters: extendedTrackingData.filters || '',
        hit_origin: origin,
        model: ad.model,
        page_name_origin: getPageNameOrigin({extendedTrackingData, origin}),
        page_type: getPageTypeByOrigin({origin}) || 'detail',
        has_pricedrop: ad.priceDrop ? 'with_price_drop' : 'without_price_drop',
        has_reservation: Boolean(ad.hasReservation),
        products: advertisementsValueObject.getProducts(),
        province: ad.province || ad.location?.mainProvince
      }
    }
  }

  _mapFinancingCalculatorEntryFeeSelectedData({ad, event}) {
    const {id, price, selectedEntry} = ad

    // eslint-disable-next-line camelcase
    const hit_information = selectedEntry

    const products = [
      {
        category: 'ads',
        id,
        price,
        quantity: 0
      }
    ]

    return {
      event,
      data: {
        hit_information,
        products
      }
    }
  }

  _mapFinancingCalculatorLoanApplicationData({ad, context, event}) {
    const {id, price, financingData} = ad

    const {entry, terms, fee, totalLoanAmount, openingExpenses, tin, tae, totalAmountDue} = financingData

    const entranceFee = entry ?? 0
    const taggingFinancingData = `entrance_fee:${entranceFee};terms:${terms};fee:${fee};vehicle_price:${price};total_loan_amount:${totalLoanAmount};opening_expenses:${openingExpenses};tin:${tin},tae:${tae};total_amount_due:${totalAmountDue}`

    const products = [
      {
        category: 'ads',
        id,
        price,
        quantity: 0
      }
    ]

    let hit_origin
    switch (context) {
      case this._trackingEventContexts.FINANCING_LOAN_APPLICATION_FDA:
        hit_origin = this._hitOrigins.FDA_CALCULATOR
        break
      case this._trackingEventContexts.FINANCING_LOAN_APPLICATION_NO_FDA:
        hit_origin = this._hitOrigins.FDA_NO_CALCULATOR
        break
    }

    return {
      event,
      data: {
        hit_origin,
        financing_data: taggingFinancingData,
        products
      }
    }
  }

  _mapFinancingCalculatorLoanApplicationFailedData({ad, event}) {
    const {id, price} = ad

    const products = [
      {
        category: 'ads',
        id,
        price,
        quantity: 0
      }
    ]

    return {
      event,
      data: {
        products
      }
    }
  }

  _mapFinancingCalculatorLoanData({ad, event}) {
    const {id, price, financingData} = ad

    const {entry, terms, fee, totalLoanAmount, openingExpenses, tin, tae, totalAmountDue} = financingData

    // eslint-disable-next-line camelcase
    const financing_data = `entrance_fee:${entry};terms:${terms};fee:${fee};vehicle_price:${price};total_loan_amount:${totalLoanAmount};opening_expenses:${openingExpenses};tin:${tin},tae:${tae};total_amount_due:${totalAmountDue}`

    const products = [
      {
        category: 'ads',
        id,
        price,
        quantity: 0
      }
    ]

    return {
      event,
      data: {
        financing_data,
        products
      }
    }
  }

  _mapFinancingCalculatorTermSelectedData({ad, event}) {
    const {id, price, selectedTerm} = ad

    // eslint-disable-next-line camelcase
    const hit_information = String(selectedTerm)

    const products = [
      {
        category: 'ads',
        id,
        price,
        quantity: 0
      }
    ]

    return {
      event,
      data: {
        hit_information,
        products
      }
    }
  }

  _mapFinancingLegalConditionsClickedData({ad, context, event}) {
    const {id, price, isProfessional} = ad

    const hit_information = isProfessional ? this._hitInformations.PROFESSIONAL_AD : this._hitInformations.PRIVATE_AD

    let hitOrigin
    switch (context) {
      case this._trackingEventContexts.FINANCING_CALCULATOR_CNET:
        hitOrigin = this._hitOrigins.FINANCING_CALCULATOR_CNET_ELEMENT
        break
      case this._trackingEventContexts.FINANCING_CALCULATOR:
        hitOrigin = this._hitOrigins.FINANCING_CALCULATOR_WIDGET_ELEMENT
        break
      case this._trackingEventContexts.FINANCING_PRICE_INFO:
        hitOrigin = this._hitOrigins.FINANCING_PRICE_ELEMENT
        break
      case this._trackingEventContexts.FINANCING_FORM_INFO:
        hitOrigin = this._hitOrigins.FINANCING_CONTACT_FORM_ELEMENT
        break
      case this._trackingEventContexts.FINANCING_FDA_CALCULATOR:
        hitOrigin = this._hitOrigins.FDA_CALCULATOR
        break
    }

    const products = [
      {
        category: 'ads',
        id,
        price,
        quantity: 0
      }
    ]

    return {
      event,
      data: {
        hit_information,
        hit_origin: hitOrigin,
        products
      }
    }
  }

  _mapFinancingPriCalculatorClickedData({ad, context, event, formName}) {
    const {id, price, carPlateData} = ad

    // eslint-disable-next-line camelcase
    const hit_information = carPlateData
      ? this._hitInformations.FINANCING_PRI_WITH_PLATE
      : this._hitInformations.FINANCING_PRI_WITHOUT_PLATE

    let hit_origin
    switch (context) {
      case this._trackingEventContexts.FINANCING_CALCULATOR:
        hit_origin = this._hitOrigins.FINANCING_CALCULATOR_WIDGET_ELEMENT
        break
      case this._trackingEventContexts.FINANCING_PRICE_INFO:
        hit_origin = this._hitOrigins.FINANCING_PRICE_ELEMENT
        break
      case this._trackingEventContexts.FINANCING_CALCULATOR_AD_DETAIL_HASH:
        hit_origin = this._hitOrigins.EMAIL
    }

    const products = [
      {
        category: 'ads',
        id,
        price,
        quantity: 0
      }
    ]

    return {
      event,
      data: {
        form_name: formName,
        hit_information,
        hit_origin,
        products
      }
    }
  }

  _mapFinancingCalculatorClickedData({ad, event}) {
    const {id, price} = ad
    const products = [
      {
        category: 'ads',
        id,
        price,
        quantity: 0
      }
    ]

    const hit_origin = this._hitOrigins.FINANCING_PRICE_ELEMENT

    return {
      event,
      data: {
        hit_origin,
        products
      }
    }
  }

  _mapTransactionBuyerPaycarSafePurchaseClicked({ad, event, pageName}) {
    const {id} = ad

    return {
      event,
      data: {
        channel: 'pay car',
        hit_origin: pageName,
        products: [
          {
            category: 'ads',
            quantity: 0,
            price: 0,
            id
          }
        ]
      }
    }
  }

  _mapTransactionPriceAgreed({ad, event}) {
    const {id, adPrice, agreedPrice} = ad

    return {
      event,
      data: {
        channel: 'pay car',
        hit_information: String(adPrice),
        ...(agreedPrice && {hit_information2: String(agreedPrice)}),
        products: [
          {
            category: 'ads',
            quantity: 0,
            price: 0,
            id
          }
        ]
      }
    }
  }

  _mapTransactionBuyerPaycarStartClicked({ad, event, pageName}) {
    const {id} = ad

    const pageNameOrigin =
      this._trackingTranslationsByCulture[this._culture][
        `${this._fromPageNameToConstantNamingPageName.map(pageName)}_PAGE_NAME`
      ]

    return {
      event,
      data: {
        channel: 'pay car',
        hit_origin: pageNameOrigin,
        products: [
          {
            category: 'ads',
            quantity: 0,
            price: 0,
            id
          }
        ]
      }
    }
  }

  _mapTransactionBuyerPaycarPaymentStartClicked({ad, event}) {
    const {id} = ad

    return {
      event,
      data: {
        channel: 'pay car',
        hit_information: 'rapidpay',
        products: [
          {
            category: 'ads',
            quantity: 0,
            price: 0,
            id
          }
        ]
      }
    }
  }

  _mapTransactionSellerPaycarSafePurchaseHandled({ad, event}) {
    const {id} = ad

    const pageNameOrigin = this._trackingTranslationsByCulture[this._culture].TRANSACTION_SELLER_ONBOARDING_PAGE_NAME

    return {
      event,
      data: {
        channel: 'pay car',
        hit_origin: pageNameOrigin,
        products: [
          {
            category: 'ads',
            quantity: 0,
            price: 0,
            id
          }
        ]
      }
    }
  }

  _mapTransactionSellerPaycarStartClicked({ad, event}) {
    const {id} = ad

    return {
      event,
      data: {
        channel: 'pay car',
        products: [
          {
            category: 'ads',
            quantity: 0,
            price: 0,
            id
          }
        ]
      }
    }
  }

  _mapTransactionLegalConditionsError({event, origin}) {
    return {
      event,
      data: {
        channel: 'pay car',
        hit_information: origin
      }
    }
  }

  _mapUserRatingCompleted({event, rating, options, comment, tradeId}) {
    return {
      event,
      data: {
        hit_information: rating,
        ...(options?.length > 0 && {hit_information2: options.join(':')}),
        ...(comment?.length > 0 && {hit_information3: comment}),
        id_trade: tradeId
      }
    }
  }

  _mapUserRatingError({event}) {
    return {
      event,
      data: {
        // Temporary error rating value until we have more information about the library Ratings
        hit_information: 'Error submitting user rating'
      }
    }
  }

  _mapVehicleConfiguratorEvents({event, origin}) {
    return {
      event,
      data: {
        hit_origin: origin
      }
    }
  }

  _mapPersonalDataFormError({event, pageError}) {
    return {
      event,
      data: {
        page_error: pageError
      }
    }
  }

  _mapValidationBuyerIdentityEventsData({ad, event, formErrors = {}, professionalActivityCode}) {
    const {id} = ad

    const hitInformation = Object.keys(formErrors)
      .filter(field => formErrors[field].error)
      .join(',')

    return {
      event,
      data: {
        ...(Object.keys(formErrors).length > 0 && {hit_information: hitInformation}),
        ...(professionalActivityCode && {hit_information: professionalActivityCode}),
        products: [
          {
            category: 'ads',
            quantity: 0,
            price: 0,
            id
          }
        ]
      }
    }
  }

  _mapValidationSellerIdentityEventsData({ad, event, formErrors = {}, pageName}) {
    const {id} = ad

    const hitInformation = Object.keys(formErrors)
      .filter(field => formErrors[field].error)
      .join(',')

    const pageNameOrigin =
      this._trackingTranslationsByCulture[this._culture][
        `${this._fromPageNameToConstantNamingPageName.map(pageName)}_PAGE_NAME`
      ]

    return {
      event,
      data: {
        hit_origin: pageNameOrigin,
        ...(Object.keys(formErrors).length > 0 && {hit_information: hitInformation}),
        products: [
          {
            category: 'ads',
            quantity: 0,
            price: 0,
            id
          }
        ]
      }
    }
  }

  _mapNewsletterSubscription({event, pageName}) {
    const {NEWSLETTER_AD_DETAIL, NEWSLETTER_HOME, NEWSLETTER_NEWS_DETAIL_BODY, NEWSLETTER_NEWS_LIST} =
      this._config.get('tracking').HIT_ORIGINS
    const {
      NEWS_DETAIL_PAGE_NAME,
      HOME_PAGE_NAME,
      ADS_DETAIL_PAGE_NAME,
      NEWS_CATEGORY_PAGE_NAME,
      NEWS_SEARCH_PAGE_NAME,
      NEWS_SECTION_TIPS_PAGE_NAME,
      NEWS_SECTION_TRENDS_PAGE_NAME,
      NEWS_TAG_PAGE_NAME
    } = this._config.get('page')

    const hitOriginsMapping = {
      [ADS_DETAIL_PAGE_NAME]: NEWSLETTER_AD_DETAIL,
      [HOME_PAGE_NAME]: NEWSLETTER_HOME,
      [NEWS_DETAIL_PAGE_NAME]: NEWSLETTER_NEWS_DETAIL_BODY,
      [NEWS_CATEGORY_PAGE_NAME]: NEWSLETTER_NEWS_LIST,
      [NEWS_SEARCH_PAGE_NAME]: NEWSLETTER_NEWS_LIST,
      [NEWS_SECTION_TIPS_PAGE_NAME]: NEWSLETTER_NEWS_LIST,
      [NEWS_SECTION_TRENDS_PAGE_NAME]: NEWSLETTER_NEWS_LIST,
      [NEWS_TAG_PAGE_NAME]: NEWSLETTER_NEWS_LIST
    }

    const hitOrigin = hitOriginsMapping[pageName] || ''

    return {
      event,
      data: {
        hit_origin: hitOrigin
      }
    }
  }

  _mapNewsCommenPublish({article, event}) {
    const {NEWS_COMMENTED} = this._config.get('tracking').HIT_ORIGINS

    return {
      event,
      data: {
        hit_information: article.title,
        hit_origin: NEWS_COMMENTED
      }
    }
  }

  _mapNewsCommenReply({article, event}) {
    const {NEWS_COMMENTED} = this._config.get('tracking').HIT_ORIGINS

    return {
      event,
      data: {
        hit_information: article.title,
        hit_origin: NEWS_COMMENTED
      }
    }
  }

  _mapAISearchUsedEvent({event, pageName, query, requestStatus = this._requestStatusAISuccess}) {
    const hitInformation2 = this._trackingEventAIMapping[requestStatus]
    return {
      event,
      data: {
        hit_origin: this._searchAiUsedHitOriginsByPageName[pageName],
        hit_information: query,
        hit_information2: hitInformation2
      }
    }
  }

  _mapPriceIndicatorClickedEvent({ad, context, event}) {
    const advertisementsValueObject = this._fromPageTrackingJsonToAdvertisementsValueObjectMapper
      .setParams({context})
      .map({ads: [ad]})

    return {
      event,
      data: {
        page_type: 'detail',
        products: advertisementsValueObject.getProducts()
      }
    }
  }

  _mapDealerServicesPopup({event, origin}) {
    return {
      event,
      data: {hit_origin: origin}
    }
  }

  _mapRatingsClickedEvent({event, origin}) {
    return {
      event,
      data: {hit_origin: origin}
    }
  }

  // The product is built here instead of through the advertisements mapper or
  // TRACKING_EVENT_DATA_DEFAULT_PRODUCT on purpose: the legacy ad detail already emits this event with
  // this exact payload, and enriching it only on the new detail would split one event name into two shapes.
  _mapReservationInformationClickedEvent({ad, event, origin}) {
    return {
      event,
      data: {
        hit_origin: origin,
        products: [{id: ad.id, category: 'ads', price: 0, quantity: 0}]
      }
    }
  }

  _mapUserConsentsClickedEvents({event, origin, status}) {
    const {CHECKED: hitInformationChecked, UNCHECKED: hitInformationUnchecked} = this._hitInformations

    return {
      event,
      data: {hit_origin: origin, hit_information: status ? hitInformationChecked : hitInformationUnchecked}
    }
  }

  _messagingEventName({eventName}) {
    if (Object.keys(this._messagingEventNames).includes(eventName)) return eventName
  }

  setParams({reset, identify}) {
    if (reset) this._reset = reset
    if (identify?.userId) this._identify = identify

    return this
  }

  map({
    ad,
    ads,
    article,
    complaintReason,
    context,
    errorType,
    event,
    extendedTrackingData,
    fieldId,
    fields,
    formErrors,
    formName,
    status,
    isPrivacyChecked,
    query,
    listPosition,
    origin,
    pageError,
    pageName,
    pageType,
    recommendation,
    requestStatus,
    vehicleData,
    vehicles,
    fda,
    ...params
  }) {
    const {
      ACCOUNT_EDITED,
      ACCOUNT_EDITED_ERROR,
      AD_DETAIL_BUTTON_CLICKED,
      AD_DISCARDED,
      AD_DISCARDED_CLICK,
      AD_FAVORITED,
      AD_IMPRESSIONS_COLLECTED_EVENT,
      AD_INSERTION_IO_ADD_TO_CALENDAR,
      AD_INSERTION_IO_BUTTON_CLICKED,
      AD_INSERTION_IO_CAR_DETAILS_ERROR,
      AD_INSERTION_IO_CAR_DETAILS_SUCCESS,
      AD_INSERTION_IO_CAR_DETAILS_VIEWED,
      AD_INSERTION_IO_CONFIRMATION_FAQ_EXPANDED,
      AD_INSERTION_IO_CONFIRMATION_VIEWED,
      AD_INSERTION_IO_DATE_CONFIRMED,
      AD_INSERTION_IO_DATE_ERROR,
      AD_INSERTION_IO_DATES_VIEWED,
      AD_INSERTION_IO_DEALER_ORDER_SELECTED,
      AD_INSERTION_IO_DEALERS_VIEWED,
      AD_INSERTION_IO_FAQ_EXPANDED,
      AD_INSERTION_IO_GO_TO_MY_ADS_CLICKED,
      AD_INSERTION_IO_MAP_CLICKED,
      AD_INSERTION_IO_MODAL_VIEWED,
      AD_INSERTION_IO_NO_INTERESA_BUTTON_CLICKED,
      AD_INSERTION_IO_NO_OFFER_AVAILABLE,
      AD_INSERTION_IO_POSTAL_CODE_CHANGED,
      AD_INSERTION_IO_PUBLISH_AD_CLICKED,
      AD_INSERTION_IO_VIDEO_PLAYED,
      AD_INSERTION_IO_WHATSAPP_ACTION,
      AD_INSERTION_MODAL_DISMISSED,
      AD_INSERTION_MODAL_VIEWED,
      AD_REPORTED,
      AD_REPORTED_ERROR,
      AD_SHARED,
      AD_SHARE_DECLINE,
      AD_SHARE_MODAL_CLOSED,
      AD_SHARE_MODAL_REJECT,
      AD_SUBSCRIPTION_PRICE_OPTION_CLICKED,
      AD_TO_COMPARE_ADDED,
      AD_TO_COMPARE_REMOVED,
      AD_TO_COMPARE_MENU_CLICKED,
      AD_TO_COMPARE_SEE_RESULTS_CLICKED,
      AD_UNFAVORITED,
      AD_VIDEO_BUTTON_CLICKED,
      AD_VIDEO_PLAYED,
      DETAIL_GALLERY_CLICKED,
      AI_SEARCH_USED,
      BUYER_PAYCAR_LEGAL_CONDITIONS_ERROR_TRANSACTION,
      BUYER_PAYCAR_PAYMENT_START_CLICKED_TRANSACTION,
      BUYER_PAYCAR_PRICE_CONFIRMED_TRANSACTION,
      BUYER_PAYCAR_PRICE_REFUSED_TRANSACTION,
      BUYER_PAYCAR_SAFE_PURCHASE_CLICKED_TRANSACTION,
      BUYER_PAYCAR_START_CLICKED_TRANSACTION,
      DEALER_SERVICES_POPUP,
      DEALER_VIDEO_PLAYED,
      FINANCING_CALCULATOR_CLICKED,
      FINANCING_CALCULATOR_ENTRY_FEE_SELECTED,
      FINANCING_CALCULATOR_ENTRY_FEE_SELECTED_B2C,
      FINANCING_CALCULATOR_LOAN_APPLICATION_CLICKED,
      FINANCING_CALCULATOR_LOAN_APPLICATION_CLICKED_B2C,
      FINANCING_CALCULATOR_LOAN_APPLICATION_FAILED_B2C,
      FINANCING_CALCULATOR_LOAN_DATA_VISUALIZED,
      FINANCING_CALCULATOR_TERM_SELECTED,
      FINANCING_CALCULATOR_TERM_SELECTED_B2C,
      FINANCING_DETAIL_LEGAL_CONDITIONS_CLICKED,
      FINANCING_PRI_CALCULATOR_CLICKED,
      INSTANT_OFFER_FIELD_SELECTED,
      INSTANT_OFFER_MODAL_VIEWED,
      LANDING_IO_FIELD_AUTOCOMPLETED,
      LANDING_IO_FIELD_SELECTED,
      LANDING_IO_FORM_FIELDS_ERROR,
      LANDING_IO_NEXT_CONFIRMED,
      LANDING_IO_NEXT_VIEWED,
      LANDING_IO_OFFER_REQUESTED,
      LANDING_IO_OFFER_REQUESTED_ERROR,
      // MODEL_DETAIL_HOME_SEARCH_CLICKED_EVENT,
      MODEL_DETAIL_HOME_CLICKED_EVENT,
      NET_ASSISTANT_REVIEW_SENT,
      NET_ASSISTANT_REVIEW_DETAIL_SENT,
      NEWSLETTER_CLICK,
      NEWS_COMMENT_PUBLISH,
      NEWS_COMMENT_REPLY,
      PERSONAL_DATA_FORM_ERROR,
      CONSUMPTION_DETAIL_CLICKED,
      EXTRA_EQUIPMENT_DETAIL_CLICKED,
      MANUFACTURER_CERTIFIED_CLICKED,
      PHOTO_DETAIL_CLICKED,
      PRICE_DETAIL_CLICKED,
      RATINGS_VIEWED_EVENT,
      SELLER_SERVICES_CLICKED,
      VEHICLE_HISTORY_CLICKED,
      FREE_VEHICLE_HISTORY_CLICKED,
      PRICE_INDICATOR_CLICKED,
      RATINGS_CLICKED,
      RECOMMENDATION_WIDGET_CLICKED,
      RECOMMENDATION_WIDGET_IMPRESSION_EVENT,
      RECOMMENDATION_WIDGET_VIEWABLE_IMPRESSION,
      REPORT_AD_VIEWED,
      RESERVATION_INFORMATION_CLICKED,
      SELLER_IDENTITY_VALIDATION_FLOW_ERROR,
      SELLER_PAYCAR_LEGAL_CONDITIONS_ERROR_TRANSACTION,
      SELLER_PAYCAR_PRICE_CONFIRMED_TRANSACTION,
      SELLER_PAYCAR_SAFE_PURCHASE_CLICKED_TRANSACTION,
      SELLER_PAYCAR_SAFE_PURCHASE_REFUSED_TRANSACTION,
      SELLER_PAYCAR_START_CLICKED_TRANSACTION,
      SIGNED_IN,
      SIGNED_IN_ERROR,
      SIGNED_UP,
      SIGNED_UP_ERROR,
      SUBSCRIPTION_CAROUSEL_CLICKED_EVENT,
      USER_CONSENT_FINANCIAL_SERVICES_CLICKED,
      USER_CONSENT_WHATSAPP_COMMUNICATIONS_CLICKED,
      USER_CONSENT_PORTAL_ANNOUNCEMENTS_CLICKED,
      USER_CONSENT_EXTERNAL_SERVICES_OFFERS_CLICKED,
      USER_CONSENT_SEGMENTED_ADS_CLICKED,
      USER_RATING_COMPLETED,
      USER_RATING_ERROR,
      VALIDATION_BUYER_IDENTITY_ADD_PERSONAL_INFORMATION_APPLICATION_ERROR_TRANSACTION,
      VALIDATION_BUYER_IDENTITY_ADD_PERSONAL_INFORMATION_CLICKED_TRANSACTION,
      VALIDATION_BUYER_IDENTITY_ADD_PROFESSIONAL_ACTIVITY_CLICKED_TRANSACTION,
      VALIDATION_BUYER_IDENTITY_ADD_PROFESSIONAL_ACTIVITY_ERROR_TRANSACTION,
      VEHICLE_CONFIGURATOR_BACK,
      VEHICLE_CONFIGURATOR_NEXT_ERROR,
      VEHICLE_CONFIGURATOR_NEXT_SUCCESS,
      VEHICLE_CONFIGURATOR_RETRY,
      VISIBILITY_PRODUCTS_BUTTON_CLICKED,
      DEALER_STOCK_SHARED_CLICKED,
      DEALER_STOCK_SHARED
    } = this._trackingEventNames

    let trackingData

    switch (event) {
      case ACCOUNT_EDITED:
      case ACCOUNT_EDITED_ERROR:
        trackingData = this._trackingEventUserProfileEditMapper.setParams({fields, formErrors}).map(event)
        break
      case AD_IMPRESSIONS_COLLECTED_EVENT:
        trackingData = this._mapAdImpressionsCollectedData({
          ads,
          context,
          event,
          vehicles,
          ...params
        })
        break
      case AD_VIDEO_PLAYED:
        trackingData = this._mapAdVideoPlayed({
          ad,
          event,
          origin
        })
        break

      case AD_DETAIL_BUTTON_CLICKED:
        trackingData = this._mapAdDetailButtonClicked({event, hitInformation: params.hitInformation})
        break

      case AD_DISCARDED:
        trackingData = this._mapAdDiscard({event, origin})
        break

      case AD_DISCARDED_CLICK:
        trackingData = this._mapAdDiscardClick({event, origin})
        break

      case AD_TO_COMPARE_ADDED:
      case AD_TO_COMPARE_REMOVED:
        trackingData = this._mapAdToCompareClick({
          ...params,
          ad,
          event,
          origin,
          extendedTrackingData,
          context,
          pageType
        })
        break

      case AD_TO_COMPARE_MENU_CLICKED:
        trackingData = this._mapAdToCompareMenuClick({...params, event, origin, ads})
        break

      case AD_TO_COMPARE_SEE_RESULTS_CLICKED:
        trackingData = this._mapAdToCompareResultsClick({...params, event, origin, ads})
        break

      case AD_UNFAVORITED:
        trackingData = this._mapAdUnfavorited({...params, event, origin})
        break

      case AD_VIDEO_BUTTON_CLICKED:
        trackingData = this._mapAdVideoButtonClicked({
          ad,
          event,
          origin
        })
        break

      case DETAIL_GALLERY_CLICKED:
        trackingData = this._mapDetailGalleryClicked({
          event,
          origin,
          hitInformation: params.hitInformation,
          hitInformation2: params.hitInformation2
        })
        break

      case PHOTO_DETAIL_CLICKED:
      case PRICE_DETAIL_CLICKED:
      case CONSUMPTION_DETAIL_CLICKED:
      case EXTRA_EQUIPMENT_DETAIL_CLICKED:
      case VEHICLE_HISTORY_CLICKED:
      case FREE_VEHICLE_HISTORY_CLICKED:
      case MANUFACTURER_CERTIFIED_CLICKED:
      case SELLER_SERVICES_CLICKED:
        trackingData = this._mapComparatorActionClicked({ad, event, origin, listPosition})
        break
      case RATINGS_VIEWED_EVENT: {
        const {COMPARADOR_PAGE_NAME} = this._trackingTranslationsByCulture[this._culture]
        trackingData = this._mapComparatorActionClicked({
          ad,
          event,
          origin,
          listPosition,
          pageName: COMPARADOR_PAGE_NAME
        })
        break
      }
      case AI_SEARCH_USED:
        trackingData = this._mapAISearchUsedEvent({event, pageName, query, requestStatus})
        break
      case DEALER_SERVICES_POPUP:
        trackingData = this._mapDealerServicesPopup({event, origin})
        break
      case DEALER_VIDEO_PLAYED:
        trackingData = this._mapDealerVideoPlayed({
          event,
          context
        })
        break
      case DEALER_STOCK_SHARED_CLICKED:
        trackingData = this._mapDealerStockSharedClicked({
          event,
          context
        })
        break
      case DEALER_STOCK_SHARED:
        trackingData = this._mapDealerStockShared({
          event,
          context
        })
        break
      case SIGNED_IN:
      case SIGNED_UP:
      case SIGNED_IN_ERROR:
      case SIGNED_UP_ERROR:
        trackingData = this._mapSignedEventsData({event, origin, context})
        break
      case AD_FAVORITED:
        trackingData = this._mapAdFavoritedData({ad, context, event, extendedTrackingData, origin})
        break

      case AD_REPORTED:
      case AD_REPORTED_ERROR:
      case REPORT_AD_VIEWED:
        trackingData = this._trackingEventAdComplaintMapper.setParams({ad, complaintReason, errorType}).map(event)
        break

      case AD_SHARED:
        trackingData = this._mapAdSharedData({
          ad,
          context,
          event,
          extendedTrackingData,
          origin
        })
        break
      case AD_SHARE_MODAL_CLOSED:
      case AD_SHARE_DECLINE:
      case AD_SHARE_MODAL_REJECT:
        trackingData = this._trackingEventFavoriteAdShareMapper.setParams({pageName}).map(event)
        break
      case AD_SUBSCRIPTION_PRICE_OPTION_CLICKED:
        trackingData = this._trackingEventAdSubscriptionPriceOptionClickedMapper
          .setParams({context, pageName})
          .map({event})
        break
      case FINANCING_CALCULATOR_ENTRY_FEE_SELECTED:
      case FINANCING_CALCULATOR_ENTRY_FEE_SELECTED_B2C:
        trackingData = this._mapFinancingCalculatorEntryFeeSelectedData({
          ad,
          event
        })
        break
      case FINANCING_CALCULATOR_LOAN_APPLICATION_CLICKED_B2C:
        trackingData = this._mapFinancingCalculatorLoanApplicationData({
          ad,
          context,
          event
        })
        break
      case FINANCING_CALCULATOR_LOAN_APPLICATION_FAILED_B2C:
        trackingData = this._mapFinancingCalculatorLoanApplicationFailedData({
          ad,
          event
        })
        break
      case FINANCING_CALCULATOR_LOAN_APPLICATION_CLICKED:
      case FINANCING_CALCULATOR_LOAN_DATA_VISUALIZED:
        trackingData = this._mapFinancingCalculatorLoanData({
          ad,
          event
        })
        break
      case FINANCING_CALCULATOR_TERM_SELECTED:
      case FINANCING_CALCULATOR_TERM_SELECTED_B2C:
        trackingData = this._mapFinancingCalculatorTermSelectedData({
          ad,
          event
        })
        break
      case FINANCING_DETAIL_LEGAL_CONDITIONS_CLICKED:
        trackingData = this._mapFinancingLegalConditionsClickedData({
          ad,
          context,
          event
        })
        break
      case FINANCING_PRI_CALCULATOR_CLICKED:
        trackingData = this._mapFinancingPriCalculatorClickedData({
          ad,
          context,
          event,
          formName
        })
        break
      case FINANCING_CALCULATOR_CLICKED:
        trackingData = this._mapFinancingCalculatorClickedData({
          ad,
          event
        })
        break
      case AD_INSERTION_IO_ADD_TO_CALENDAR:
      case AD_INSERTION_IO_BUTTON_CLICKED:
      case AD_INSERTION_IO_CAR_DETAILS_ERROR:
      case AD_INSERTION_IO_CAR_DETAILS_SUCCESS:
      case AD_INSERTION_IO_CAR_DETAILS_VIEWED:
      case AD_INSERTION_IO_CONFIRMATION_FAQ_EXPANDED:
      case AD_INSERTION_IO_CONFIRMATION_VIEWED:
      case AD_INSERTION_IO_DATE_CONFIRMED:
      case AD_INSERTION_IO_DATE_ERROR:
      case AD_INSERTION_IO_DATES_VIEWED:
      case AD_INSERTION_IO_DEALER_ORDER_SELECTED:
      case AD_INSERTION_IO_DEALERS_VIEWED:
      case AD_INSERTION_IO_FAQ_EXPANDED:
      case AD_INSERTION_IO_GO_TO_MY_ADS_CLICKED:
      case AD_INSERTION_IO_MAP_CLICKED:
      case AD_INSERTION_IO_MODAL_VIEWED:
      case AD_INSERTION_IO_NO_INTERESA_BUTTON_CLICKED:
      case AD_INSERTION_IO_NO_OFFER_AVAILABLE:
      case AD_INSERTION_IO_POSTAL_CODE_CHANGED:
      case AD_INSERTION_IO_PUBLISH_AD_CLICKED:
      case AD_INSERTION_IO_VIDEO_PLAYED:
      case AD_INSERTION_IO_WHATSAPP_ACTION:
      case AD_INSERTION_MODAL_DISMISSED:
      case AD_INSERTION_MODAL_VIEWED:
      case INSTANT_OFFER_FIELD_SELECTED:
      case INSTANT_OFFER_MODAL_VIEWED:
        trackingData = this._trackingEventsInstantOfferMapper
          .setParams({ad, errorType, fieldId, formErrors, origin, ...params})
          .map(event)
        break
      case LANDING_IO_FIELD_SELECTED:
      case LANDING_IO_FIELD_AUTOCOMPLETED:
      case LANDING_IO_FORM_FIELDS_ERROR:
      case LANDING_IO_NEXT_CONFIRMED:
      case LANDING_IO_NEXT_VIEWED:
      case LANDING_IO_OFFER_REQUESTED:
      case LANDING_IO_OFFER_REQUESTED_ERROR:
        trackingData = this._trackingEventLandingInstantOfferMapper
          .setParams({fieldId, formErrors, isPrivacyChecked, vehicleData})
          .map(event)
        break

      case MODEL_DETAIL_HOME_CLICKED_EVENT:
        trackingData = this._mapModelDetailHomeClicked({ad, event})
        break

      case NET_ASSISTANT_REVIEW_SENT:
      case NET_ASSISTANT_REVIEW_DETAIL_SENT:
        trackingData = this._mapAdsAndFiltersData({
          event,
          ads,
          ...params
        })
        break

      case NEWSLETTER_CLICK:
        trackingData = this._mapNewsletterSubscription({event, pageName})
        break

      case NEWS_COMMENT_PUBLISH:
        trackingData = this._mapNewsCommenPublish({article, event})
        break

      case NEWS_COMMENT_REPLY:
        trackingData = this._mapNewsCommenReply({article, event})
        break

      case this._messagingEventName({eventName: event}):
        trackingData = {event: this._messagingEventNames[event]}
        break

      case PERSONAL_DATA_FORM_ERROR:
        trackingData = this._mapPersonalDataFormError({event, pageError})
        break

      case RECOMMENDATION_WIDGET_CLICKED:
        trackingData = this._mapRecommendationWidgetClicked({
          event,
          recommendation,
          ads,
          context
        })
        break

      case RECOMMENDATION_WIDGET_IMPRESSION_EVENT:
      case RECOMMENDATION_WIDGET_VIEWABLE_IMPRESSION:
        trackingData = this._mapRecommendationWidgetImpression({
          event,
          recommendation,
          ads,
          context
        })
        break

      case SUBSCRIPTION_CAROUSEL_CLICKED_EVENT:
        trackingData = this._mapCarouselClickedEvent({context, event})
        break

      case PRICE_INDICATOR_CLICKED:
        trackingData = this._mapPriceIndicatorClickedEvent({ad, context, event})
        break
      case RATINGS_CLICKED:
        trackingData = this._mapRatingsClickedEvent({event, origin})
        break
      case RESERVATION_INFORMATION_CLICKED:
        trackingData = this._mapReservationInformationClickedEvent({ad, event, origin})
        break
      case SELLER_IDENTITY_VALIDATION_FLOW_ERROR:
        trackingData = this._mapValidationSellerIdentityEventsData({
          ad,
          event,
          formErrors,
          pageName
        })
        break
      case BUYER_PAYCAR_SAFE_PURCHASE_CLICKED_TRANSACTION:
        trackingData = this._mapTransactionBuyerPaycarSafePurchaseClicked({
          ad,
          event,
          pageName
        })
        break
      case BUYER_PAYCAR_PRICE_REFUSED_TRANSACTION:
      case BUYER_PAYCAR_PRICE_CONFIRMED_TRANSACTION:
      case SELLER_PAYCAR_PRICE_CONFIRMED_TRANSACTION:
        trackingData = this._mapTransactionPriceAgreed({
          ad,
          event
        })
        break
      case BUYER_PAYCAR_START_CLICKED_TRANSACTION:
        trackingData = this._mapTransactionBuyerPaycarStartClicked({
          ad,
          event,
          pageName
        })
        break
      case BUYER_PAYCAR_PAYMENT_START_CLICKED_TRANSACTION:
        trackingData = this._mapTransactionBuyerPaycarPaymentStartClicked({
          ad,
          event
        })
        break
      case BUYER_PAYCAR_LEGAL_CONDITIONS_ERROR_TRANSACTION:
      case SELLER_PAYCAR_LEGAL_CONDITIONS_ERROR_TRANSACTION:
        trackingData = this._mapTransactionLegalConditionsError({
          event,
          origin
        })
        break
      case SELLER_PAYCAR_SAFE_PURCHASE_CLICKED_TRANSACTION:
      case SELLER_PAYCAR_SAFE_PURCHASE_REFUSED_TRANSACTION:
        trackingData = this._mapTransactionSellerPaycarSafePurchaseHandled({ad, event})
        break
      case SELLER_PAYCAR_START_CLICKED_TRANSACTION:
        trackingData = this._mapTransactionSellerPaycarStartClicked({ad, event})
        break
      case USER_CONSENT_FINANCIAL_SERVICES_CLICKED:
      case USER_CONSENT_WHATSAPP_COMMUNICATIONS_CLICKED:
      case USER_CONSENT_PORTAL_ANNOUNCEMENTS_CLICKED:
      case USER_CONSENT_EXTERNAL_SERVICES_OFFERS_CLICKED:
      case USER_CONSENT_SEGMENTED_ADS_CLICKED:
        trackingData = this._mapUserConsentsClickedEvents({event, origin, status})
        break
      case VALIDATION_BUYER_IDENTITY_ADD_PERSONAL_INFORMATION_APPLICATION_ERROR_TRANSACTION:
      case VALIDATION_BUYER_IDENTITY_ADD_PERSONAL_INFORMATION_CLICKED_TRANSACTION:
      case VALIDATION_BUYER_IDENTITY_ADD_PROFESSIONAL_ACTIVITY_CLICKED_TRANSACTION:
      case VALIDATION_BUYER_IDENTITY_ADD_PROFESSIONAL_ACTIVITY_ERROR_TRANSACTION:
        trackingData = this._mapValidationBuyerIdentityEventsData({
          ...params,
          ad,
          event,
          formErrors
        })
        break

      case USER_RATING_COMPLETED:
        trackingData = this._mapUserRatingCompleted({
          event,
          ...params
        })
        break

      case USER_RATING_ERROR:
        trackingData = this._mapUserRatingError({event})
        break

      case VEHICLE_CONFIGURATOR_NEXT_SUCCESS:
      case VEHICLE_CONFIGURATOR_NEXT_ERROR:
      case VEHICLE_CONFIGURATOR_BACK:
      case VEHICLE_CONFIGURATOR_RETRY:
        trackingData = this._mapVehicleConfiguratorEvents({event, origin, ...params})
        break

      case VISIBILITY_PRODUCTS_BUTTON_CLICKED:
        trackingData = this._trackingEventsVisibilityProductsMapper.setParams({...params}).map(event)
        break

      default:
        trackingData = {event}
        break
    }

    return this._valueObject({
      ...trackingData,
      reset: this._reset,
      identify: this._identify
    })
  }
}
